// Dashed rij met + icoon; standaard inline quick-add (zelfde parser als QuickBar).
import { useState, useRef, useCallback } from 'react';
import { Typography, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useT } from '@/i18n/useT';
import { useQuickbarPlaceholder } from '@/hooks/ui/useQuickbarPlaceholder';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { mergeNewTaskCreateData } from '@/features/tasks/context/tasksContextUtils';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';
import TaskInputInline from '@/features/tasks/components/forms/TaskInputInline';
import { getKeysAtIndex } from '@/features/tasks/utils/structure/taskHierarchy';
import { safeGenerateKeyBetween } from '@/features/tasks/utils/interactions/listKeyHelpers';
import { AddTaskInputRow } from '@/components/ui/AddTaskInputRow';
import { getOnboardingHighlightSx } from '@/features/onboarding/utils/onboardingHighlightSx';

export { AddTaskInputRow } from '@/components/ui/AddTaskInputRow';

const TRIGGER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  py: 2,
  px: 2.5,
  mb: 1.5,
  width: '100%',
  border: 1,
  borderStyle: 'dashed',
  color: 'text.subtle',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { borderColor: 'text.secondary' },
  '&:active': { backgroundColor: 'background.paper' },
};

/**
 * @param {object} [props]
 * @param {() => void} [props.onClick] — custom trigger (bv. TaskForm pending subtasks)
 * @param {import('@/types').Task[]} [props.tasks] — zichtbare lijst; bepaalt append-positie
 * @param {string | null} [props.parentId] — expliciete parent (root append wanneer weggelaten)
 * @param {Record<string, unknown>} [props.defaultTaskData] — merged na parseTaskInput
 * @param {string} [props.label] — override default "Taak toevoegen" label
 * @param {boolean} [props.highlight] — onboarding outline-pulse
 * @param {() => void} [props.onOpen] — fired when the inline add UI opens
 */
export function AddTaskRow({
  onClick,
  tasks = [],
  parentId,
  defaultTaskData,
  label,
  tabIndex: tabIndexProp = 0,
  highlight = false,
  onOpen,
}) {
  const t = useT();
  const quickbarPlaceholder = useQuickbarPlaceholder();
  const { user } = useAuth();
  const { projectFilter, contextFilter } = useTasksContext();
  const { addTask } = useAddTask();
  const [isAdding, setIsAdding] = useState(false);
  const appendPositionRef = useRef(null);

  const resolveAppendPosition = useCallback(() => {
    // Order keys from end-of-list; parentId must NOT inherit the last visible
    // row (that may be a child). General "taak toevoegen" = root append unless
    // parentId / defaultTaskData.parentId is set explicitly.
    const base = getKeysAtIndex(tasks.length, tasks);
    return {
      ...base,
      parentId: parentId ?? defaultTaskData?.parentId ?? null,
    };
  }, [tasks, parentId, defaultTaskData?.parentId]);

  const openInlineInput = useCallback(() => {
    appendPositionRef.current = resolveAppendPosition();
    setIsAdding(true);
    onOpen?.();
  }, [resolveAppendPosition, onOpen]);

  const displayLabel = label ?? t('shared.addTask', 'Taak toevoegen');
  const triggerSx = { ...TRIGGER_SX, ...getOnboardingHighlightSx(highlight) };

  if (onClick) {
    return (
      <Paper
        component="button"
        type="button"
        variant="outlined"
        onClick={onClick}
        tabIndex={tabIndexProp}
        sx={triggerSx}
        className="add-task-row-trigger"
        data-testid="add-task-row"
        data-onboarding-highlight={highlight ? 'true' : undefined}
      >
        <AddIcon sx={{ fontSize: 14 }} />
        <Typography variant="body2" color="text.subtle">
          {displayLabel}
        </Typography>
      </Paper>
    );
  }

  if (isAdding) {
    return (
      <AddTaskInputRow>
        <TaskInputInline
          dense={false}
          placeholder={quickbarPlaceholder}
          onSave={async (parsedData) => {
            if (!user) {
              console.error('Cannot add task: user not logged in.');
              return;
            }
            const position =
              appendPositionRef.current ?? resolveAppendPosition();
            await addTask(
              {
                ...mergeNewTaskCreateData(
                  defaultTaskData,
                  parsedData,
                  projectFilter,
                  contextFilter,
                ),
                parentId: position.parentId,
              },
              position,
            );

            if (
              position.previousKey !== undefined ||
              position.nextKey !== undefined
            ) {
              try {
                const optimisticKey = safeGenerateKeyBetween(
                  position.previousKey ?? null,
                  position.nextKey ?? null,
                );
                appendPositionRef.current = {
                  ...position,
                  previousKey: optimisticKey,
                };
              } catch (e) {
                console.error('Failed to compute optimistic key', e);
              }
            }
          }}
          onCancel={() => {
            appendPositionRef.current = null;
            setIsAdding(false);
          }}
        />
      </AddTaskInputRow>
    );
  }

  return (
    <Paper
      component="button"
      type="button"
      variant="outlined"
      onClick={openInlineInput}
      tabIndex={tabIndexProp}
      sx={triggerSx}
      className="add-task-row-trigger"
      data-testid="add-task-row"
      data-onboarding-highlight={highlight ? 'true' : undefined}
    >
      <AddIcon sx={{ fontSize: 14 }} />
      <Typography variant="body2" color="text.subtle">
        {displayLabel}
      </Typography>
    </Paper>
  );
}
